/**
 * Wave 24u.3 — Parent view-mode switcher.
 *
 * Small pill toggle rendered in the top bar for admin/coach/staff who
 * ALSO own players. Clicking flips viewing mode and reloads the app.
 *
 * Hidden entirely when:
 *   • The user has no linked players (backend eligibility says so).
 *   • The user's real role is already `parent` (no toggle needed).
 *   • Eligibility hasn't loaded yet (avoid flashing an incorrect state).
 */
import { UserCog, Users } from 'lucide-react';
import { useViewMode } from '../context/ViewModeContext';

const roleLabel = (role) => {
  if (role === 'admin') return 'Admin';
  if (role === 'coach') return 'Coach';
  if (role === 'staff') return 'Staff';
  return 'Admin';
};

export const ViewModeSwitcher = () => {
  const { canSwitchToParent, isParentMode, realRole, setViewMode } = useViewMode();

  if (!canSwitchToParent) return null;

  const activePillClasses =
    'bg-blue-600 text-white shadow-sm';
  const inactivePillClasses =
    'bg-transparent text-gray-600 hover:bg-gray-100';

  return (
    <div
      className="hidden sm:inline-flex items-center rounded-full border border-gray-200 bg-gray-50 p-0.5 text-xs font-medium"
      role="group"
      aria-label="Switch view mode"
      data-testid="view-mode-switcher"
    >
      <button
        type="button"
        onClick={() => !isParentMode ? null : setViewMode(null)}
        aria-pressed={!isParentMode}
        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 transition-colors ${
          !isParentMode ? activePillClasses : inactivePillClasses
        }`}
        data-testid="view-mode-real-role"
        title={`Switch to ${roleLabel(realRole)} view`}
      >
        <UserCog size={14} />
        {roleLabel(realRole)}
      </button>
      <button
        type="button"
        onClick={() => isParentMode ? null : setViewMode('parent')}
        aria-pressed={isParentMode}
        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 transition-colors ${
          isParentMode ? activePillClasses : inactivePillClasses
        }`}
        data-testid="view-mode-parent"
        title="Switch to Parent view"
      >
        <Users size={14} />
        Parent
      </button>
    </div>
  );
};

/**
 * Slim banner shown at the top when the user is viewing as a parent.
 * Reminds them their real role is different and lets them switch back
 * with one click.
 */
export const ParentModeBanner = () => {
  const { isParentMode, realRole, setViewMode } = useViewMode();
  if (!isParentMode) return null;
  return (
    <div
      className="w-full bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-900 flex items-center justify-between"
      data-testid="parent-mode-banner"
    >
      <div className="flex items-center gap-2">
        <Users size={14} className="text-amber-700" />
        <span>
          You&apos;re viewing as a <strong>Parent</strong>. Admin, coach and staff
          features are hidden until you switch back.
        </span>
      </div>
      <button
        type="button"
        onClick={() => setViewMode(null)}
        className="ml-4 underline underline-offset-2 hover:text-amber-950"
        data-testid="parent-mode-banner-exit"
      >
        Switch back to {roleLabel(realRole)}
      </button>
    </div>
  );
};

export default ViewModeSwitcher;
